// Core Business Types
export interface Business {
  id: string;
  name: string;
  about?: string;
  logoUrl?: string;
  headerImageUrl?: string;
  selectAnyStaffForOnlineBooking: boolean;
  headerImageUrls?: Array<{ imageUrl: string }>;
  acceptsOnlineBookings: boolean;
  allowServiceStaffSelection: boolean;
  allowGroupBooking?: boolean;
  singleStaffBooking: boolean;
  showPromosOnBookingSite?: boolean;
  showFeaturedServicesOnBookingSite?: boolean;
  productStorefrontEnabled?: boolean;
  productStorefrontAvailable?: boolean;
  listProductsOnBookingSite?: boolean;
  giftCardSettingForBooking?: GiftCardSetting | null;
  country?: {
    currency?: Currency | Currency[];
  };
  businessSpecialties?: string[];
  urlIdentifier?: string;
  bookingUrlIdentifier?: string;
  cancellationPolicy?: string;
  timezone?: string;
  callingCode?: string;
  countryCode?: string;
  bookingLeadTimeMinutes?: number;
  maxAdvanceBookingDays?: number;
  cancelRescheduleCutoffMinutes?: number;
}

export interface Currency {
  symbol: string;
  code: string;
  factor?: number;
}

export interface GiftCardSetting {
  id: string;
  name?: string | null;
  description?: string | null;
  enabled: boolean;
  minAmount?: number | null;
  maxAmount?: number | null;
  online?: boolean;
  inStore?: boolean;
}

export interface Salon {
  id: string;
  timezone: string;
  branchName: string;
  address: string;
  city: string;
  state: string;
  phone?: string;
  callingCode?: string;
  countryCode?: string;
  email?: string;
  latitude?: number;
  longitude?: number;
  bookingLink?: string;
  urlIdentifier?: string;
  status?: string;
  locationType?: string;
  bearsProcessingFee?: boolean;
  isTaxVisible?: boolean;
  taxInclusivePricing?: boolean;
  salonHours?: SalonHour[];
  closedPeriods?: ClosedPeriod[];
  salonStaffs?: SalonStaff[];
  services?: Service[];
  packages?: Package[];
  bundles?: Bundle[];
  products?: Product[];
}

export interface SalonHour {
  id: string;
  day: string;
  openTime?: string;
  closeTime?: string;
  isClosed?: boolean;
}

export interface ClosedPeriod {
  id: string;
  title?: string;
  startAt: string;
  endAt: string;
}

// Service Types
export interface ServiceCategory {
  id: string;
  name: string;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
  orderIndex?: number;
}

export interface ServiceVariant {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration: number;
  serviceId: string;
  active: boolean;
  sortOrder?: number;
  isDepositRequired?: boolean;
  depositType?: string;
  depositValue?: number;
}

export interface ServicePromotion {
  id?: string;
  title?: string;
  description?: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  startAt?: string;
  endAt?: string;
  isActive?: boolean;
}

export interface Service {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration: number; // in minutes
  extraTime?: number | null; // in minutes
  categoryId?: string;
  category?: ServiceCategory;
  isPublic: boolean;
  featured?: boolean;
  featuredIndex?: number | null;
  pricingType?: "fixed" | "starting_at" | "variable";
  serviceStaffs?: ServiceStaff[];
  quantity?: number;
  voucherServiceData?: VoucherService; // For voucher services to store redemption info
  hasVariants?: boolean;
  serviceVariants?: ServiceVariant[];
  promotion?: ServicePromotion;
  originalPrice?: number; // Original price before promotion
}

export type BundleScheduleType = "sequence" | "parallel";

export interface BundleService {
  id: string;
  serviceId?: string;
  quantity: number;
  sortOrder?: number;
  service?: Service;
}

export interface Bundle {
  id: string;
  name: string;
  description?: string;
  active?: boolean;
  status?: string;
  allowSaleOn?: "all" | "salon" | "online";
  category?: {
    id: string;
    name: string;
    description?: string | null;
  } | null;
  totalPrice: number;
  customPrice?: number | null;
  usesCustomPrice?: boolean | null;
  discountPercentage?: number | null;
  scheduleType?: BundleScheduleType | string | null;
  bundleServices?: BundleService[];
}

export interface ServiceStaff {
  id: string;
  serviceId: string;
  salonStaff?: SalonStaff;
  price?: number;
}

export interface GroupedServices {
  name: string;
  category: string;
  description?: string | null;
  show: boolean;
  services: BookingService[];
}

export interface BookingService extends Service {
  selected?: boolean;
  quantity?: number;
  staff?: SalonStaff | null;
  staffIds?: string[];
  staffPrice?: number;
  bundleId?: string | null;
  bundleScheduleType?: BundleScheduleType | null;
  // Variant properties
  variantId?: string;
  variantName?: string;
  variantPrice?: number;
  variantDuration?: number;
  /** Client-side guest id; undefined means Me (or legacy single-person cart). */
  guestId?: string;
}

/** Attendee in a group booking cart. Position 1 is always Me. */
export interface BookingGuest {
  id: string;
  position: number;
  name?: string;
}

/** Max additional guests beyond Me (positions 2..6). */
export const MAX_ADDITIONAL_GUESTS = 5;

// Staff Types
export interface SalonStaff {
  id: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    fullName?: string;
    email?: string;
    avatar?: string;
  };
  rating?: number;
  specialization?: string;
  specialty?: string;
  available?: boolean;
  active?: boolean;
  canServeCustomers?: boolean;
}

// Package/Voucher Types
export interface Package {
  id: string;
  name: string;
  description?: string;
  totalPrice: number;
  customPrice?: number;
  usesCustomPrice?: boolean;
  validity?: number; // days
  packageServices?: PackageService[];
  allowSaleOn?: "all" | "salon" | "online";
  isPurchasableAsVoucher?: boolean;
  validityMonths?: number;
}

export interface PackageService {
  id: string;
  serviceId: string;
  service?: Service;
  quantity: number;
}

export interface BookingPackage extends Package {
  quantity: number;
  selected?: boolean;
}

export interface ProductCategory {
  id: string;
  name: string;
}

export interface Product {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  retailPrice?: number | null;
  stockCount?: number | null;
  inStock?: boolean | null;
  listOnBookingSite?: boolean | null;
  isPurchasable?: boolean | null;
  expirationDate?: string | null;
  status?: string | null;
  salonId?: string | null;
  category?: ProductCategory | null;
}

export interface BookingProduct extends Product {
  quantity: number;
  selected?: boolean;
}

export interface ProductOrderSummary {
  invoiceNumber?: string | null;
  paymentReference?: string | null;
  totalPaid?: number | null;
  message?: string | null;
  status?: string | null;
}

export interface PackageVoucher {
  id: string;
  code: string;
  packageId: string;
  package?: Package;
  client?: Client;
  salonId: string;
  voucherServices?: VoucherService[];
  expiresAt?: string;
  usedAt?: string | null;
}

export interface VoucherService {
  id: string;
  service?: Service;
  serviceId?: string;
  name?: string;
  price?: number;
  quantity: number;
  used: number;
  isRedeemed: boolean;
  cartQuantity?: number;
}

// Client/Customer Types
export interface Client {
  id?: string;
  fullName: string;
  firstName?: string;
  locationType?: string;
  lastName?: string;
  email: string;
  originalEmail?: string; // Track original email to detect changes
  phone: string;
  countryCode: string;
  callingCode: string;
  note?: string;
  address?: string;
  isExistingClient?: boolean;
  walletBalance?: number;
  membershipWalletBalance?: number;
  clientMembership?: any;
}

// Appointment Types
export interface AppointmentService {
  id: string;
  serviceId: string;
  service?: Service;
  appointmentServiceStaff?: Array<{
    salonStaff?: SalonStaff;
  }>;
}

export interface Appointment {
  id: string;
  bookingId?: string;
  client?: Client;
  salon?: Salon;
  timezone: string;
  startAt: string;
  endAt: string;
  appointmentServices?: AppointmentService[];
  status: "pending" | "confirmed" | "completed" | "cancelled" | "no_show";
  totalAmount?: number;
  deposit?: number;
  notes?: string;
}

export interface AppointmentPreview {
  addOnAmount?: number;
  depositAmount?: number;
  cancellationFeeMessages?: string[];
  processingFeeAmount?: number;
  depositProcessingFeeAmount?: number;
  taxAmount?: number;
  subTotal?: number;
  totalPaid?: number;
  totalPoints?: number;
  totalPrice?: number;
  totalProductsAmount?: number;
  totalServicesAmount?: number;
  loyaltyDiscountAmount?: number;
  discountAmount?: number;
  cancellationFee?: number;
  cancellationFeeMessage?: string;
  appointmentPromo?: {
    amount: number;
    title: string;
  };
  feedbackMessages?: Array<{
    message: string;
    type: string;
  }>;
  services?: Array<{
    name: string;
    price: number;
    quantity: number;
  }>;
}

// Promo Types
export interface Promo {
  id: string;
  code: string;
  title: string;
  description?: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  startAt?: string;
  endAt?: string;
  minimumPurchase?: number;
  validFrom?: string;
  validTo?: string;
  maxUses?: number;
  currentUses?: number;
}

export interface ClientMilestone {
  id: string;
  name: string;
  description?: string;
  points?: number;
  discount?: number;
  type: "points" | "visits" | "spending";
}

// Form Types
export interface BookingFormData {
  fullName: string;
  email: string;
  phone: string;
  countryCode: string;
  callingCode: string;
  note?: string;
  marketingOptIn?: boolean;
}

// Context Types
export interface BookingContextType {
  // State
  step: number;
  services: Service[];
  popularServices: Service[];
  bundles: Bundle[];
  selectedBundles: Bundle[];
  formattedServices: GroupedServices[];
  formattedPackages: BookingPackage[];
  selectedServices: BookingService[];
  selectedPackages: BookingPackage[];
  products: Product[];
  selectedProducts: BookingProduct[];
  giftCardCart: GiftCardCartItem | null;
  productOrder: ProductOrderSummary | null;
  guests: BookingGuest[];
  activeGuestId: string | null;
  selectedStaff: SalonStaff | null;
  availableServiceStaffs: SalonStaff[];
  businessInfo: Business | null;
  salons: Salon[];
  selectedSalon: Salon | null;
  selectedSalonId: string | null;
  appointmentDateTime: string | null;
  priceSummary: AppointmentPreview | null;
  packagesPriceSummary: any | null;
  serviceClient: Client | null;
  voucherToBeRedeemed: PackageVoucher | null;
  promoCodeApplication: any | null;
  promoCode: string | null;
  rewards: ClientMilestone[];
  selectedReward: ClientMilestone | null;
  activeTab: string;
  openVoucherRedemptionModal: boolean;
  bookedAppointment: Appointment | null;
  appointmentPaymentType: string | null;
  actualAmountPaid: number | null;
  actualProcessingFee: number | null;
  staffOption: string | null;
  debouncedSearchQuery: string;
  searchCount: number;
  searchWrapperIsOpen: boolean;
  rewardSelected: boolean;
  faqs: any[];
  reviews: any[];
  isMobile: boolean;
  existingAppointment: Appointment | null;
  loadingServices: boolean;
  promos: Promo[];
  locationType: string | null;
  // Workflow Form State
  workflowForm: WorkflowForm | null;
  workflowFormAnswers: WorkflowFormAnswers;
  showWorkflowFormModal: boolean;
  workflowFormSubmissionId: string | null;
  // Selected time slot for availability polling (to detect double-booking)
  selectedDate: string | null; // "YYYY-MM-DD" format
  selectedTime: string | null; // "HH:MM" format
  // Slot reservation timer (5-minute countdown from slot selection)
  slotSelectionTime: number | null; // Timestamp when slot was selected
  // Auto-trigger next-available search after slot/resource race on confirm
  shouldFindNextAvailable: boolean;
  // Actions
  handleNextStep: (page?: number) => void;
  setFormattedServices: (services: GroupedServices[]) => void;
  showOrHideCategory: (categoryName: string, show?: boolean) => void;
  selectService: (serviceId: string) => void;
  selectServiceWithVariant: (
    serviceId: string,
    variant?: ServiceVariant | null
  ) => void;
  selectBundle: (bundleId: string) => void;
  clearSelectedBundles: () => void;
  setSelectedServices: (services: BookingService[]) => void;
  addGuest: (name?: string) => BookingGuest | null;
  removeGuest: (guestId: string) => void;
  renameGuest: (guestId: string, name: string) => void;
  setActiveGuestId: (guestId: string | null) => void;
  ensureMeGuest: () => BookingGuest;
  guestLabel: (guest: BookingGuest) => string;
  setSelectedSalonId: (id: string | null) => void;
  setAppointmentDateTime: (dateTime: string | null) => void;
  setServiceClient: (client: Client | null) => void;
  setActiveTab: (tab: BookingTabName) => void;
  setPriceSummary: (summary: AppointmentPreview | null) => void;
  clearServicesInCart: () => void;
  setVoucherToBeRedeemed: (voucher: PackageVoucher | null) => void;
  setLocationType: (locationType: string) => void;
  setOpenVoucherRedemptionModal: (open: boolean) => void;
  setBookedAppointment: (appointment: Appointment | null) => void;

  setPromoCodeApplication: (promo: any) => void;
  setPromoCode: (code: string | null) => void;
  setPackagesPriceSummary: (summary: any) => void;
  setAppointmentPaymentType: (type: string | null) => void;
  setActualAmountPaid: (amount: number | null) => void;
  setActualProcessingFee: (fee: number | null) => void;
  setStaffOption: (option: string | null) => void;
  setRewards: (rewards: ClientMilestone[]) => void;
  setSelectedReward: (reward: ClientMilestone | null) => void;
  closeSearch: () => void;
  openSearchWrapper: (box?: boolean) => void;
  setDebouncedSearchQuery: (query: string) => void;
  setRewardSelected: (selected: boolean) => void;
  deleteService: (serviceId: string, guestId?: string) => void;
  setSelectedStaff: (staff: SalonStaff | null) => void;
  setAvailableServiceStaffs: (staffs: SalonStaff[]) => void;
  modifyPackageInCart: (
    packageId: string,
    action: "add" | "remove" | "clear"
  ) => void;
  modifyServiceInCart: (
    serviceId: string,
    action: "add" | "remove" | "clear",
    guestId?: string
  ) => void;
  setGiftCardCart: (giftCard: GiftCardCartItem | null) => void;
  updateGiftCardCartQuantity: (quantity: number) => void;
  clearGiftCardCart: () => void;
  modifyProductInCart: (
    productId: string,
    action: "add" | "remove" | "clear"
  ) => void;
  updateProductCartQuantity: (productId: string, quantity: number) => void;
  clearProductCart: () => void;
  setProductOrder: (order: ProductOrderSummary | null) => void;
  addToast: (toast: {
    message: string;
    variant: "success" | "error" | "warning";
  }) => void;
  scrollToTop: () => void;
  resetBooking: () => void;
  // Workflow Form Actions
  setWorkflowForm: (form: WorkflowForm | null) => void;
  setWorkflowFormAnswers: (answers: WorkflowFormAnswers) => void;
  setShowWorkflowFormModal: (show: boolean) => void;
  setWorkflowFormSubmissionId: (id: string | null) => void;
  updateWorkflowFormAnswer: (fieldId: string, value: string | string[] | boolean) => void;
  clearWorkflowForm: () => void;
  // Actions for selected time slot (for availability polling)
  setSelectedDate: (date: string | null) => void;
  setSelectedTime: (time: string | null) => void;
  clearSelectedDateTime: () => void;
  // Actions for slot reservation timer
  setSlotSelectionTime: (time: number | null) => void;
  setShouldFindNextAvailable: (value: boolean) => void;
  deleteHeldAppointment: (
    appointment: Appointment,
    salonId: string
  ) => Promise<void>;
}

// Booking Steps
export enum BookingStep {
  LANDING = -1,
  INITIAL = 0,
  STAFF_SELECTION = 1,
  TIME_AND_DATE = 2,
  PHONE_FORM = 3,
  PAYMENT = 4,
  SUCCESS = 5,
  VOUCHER_CHECKOUT = 6,
  PURCHASE_SUCCESSFUL = 7,
  GIFT_CARD_CHECKOUT = 8,
  PRODUCT_CHECKOUT = 9,
  GUESTS_AND_SERVICES = 10,
}

// Tab Names
export enum BookingTabName {
  SERVICES = "services",
  VOUCHERS = "vouchers",
}

// Voucher Purchase Types
export interface VoucherPackage {
  packageId: string;
  quantity: number;
}

export interface VoucherRecipient {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  callingCode: string;
  phone: string;
  packages: VoucherPackage[];
}

export interface VoucherPurchaseInput {
  recipients: VoucherRecipient[];
  salonId: string;
  paymentProvider?: string;
  channels?: string[];
}

// Gift Card Purchase Types
export interface GiftCardRecipient {
  firstName: string;
  lastName?: string;
  email?: string;
  countryCode: string;
  callingCode: string;
  phone: string;
}

export interface GiftCardPurchaseInput {
  salonId: string;
  amount: number;
  recipient: GiftCardRecipient;
  buyer?: GiftCardRecipient;
  paymentMethod?: string;
  paymentProvider?: string;
  channels?: string[];
  message?: string;
}

export interface GiftCardCartItem {
  amount: number;
  message: string;
  quantity: number;
}

export type BusinessSpecialty = {
  id: string;
  name?: string | null;
  description?: string | null;
};

// Workflow Form Types
export interface WorkflowFormField {
  id: string;
  fieldType:
    | "short_text"
    | "long_text"
    | "single_choice"
    | "multiple_choice"
    | "date"
    | "number"
    | "email"
    | "phone"
    | "file"
    | "signature";
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  description?: string;
  order?: number;
}

export interface WorkflowForm {
  id: string;
  submissionId?: string;
  name: string;
  description?: string;
  fields: WorkflowFormField[];
  status?: string;
  sendCondition?:
    | "BEFORE_BOOKING"
    | "AFTER_BOOKING_CONFIRMED"
    | "AFTER_APPOINTMENT";
  services?: string[];
}

export interface WorkflowFormAnswers {
  [fieldId: string]: string | string[] | boolean;
}

export interface WorkflowFormState {
  form: WorkflowForm | null;
  answers: WorkflowFormAnswers;
  isOpen: boolean;
  isSubmitting: boolean;
  submissionId?: string;
}
